/**
 * Shared Google Maps script loader.
 * Uses a named callback so we know exactly when the Maps API is fully ready.
 * The single module-level promise prevents duplicate script injection across
 * components (PublicShopMap, LocationPickerPage, ShopsMapPage, etc.).
 */

let _mapsPromise: Promise<void> | null = null

/**
 * Set when Google rejects the key itself.
 *
 * The script LOADS fine with a bad key — the rejection happens later, per map,
 * and Google paints its own grey "Oops! Something went wrong" panel over the
 * container. That panel names no cause, so the usual reading of it is "the app
 * is broken" when the truth is almost always one line of configuration: the
 * site's domain is not on the key's HTTP-referrer allowlist. `gm_authFailure`
 * is the documented hook Google calls at that moment, and it is global, so it
 * is registered once here rather than in each of the six map components.
 */
let authFailed = false
const authListeners = new Set<(message: string) => void>()

export const MAPS_AUTH_MESSAGE =
  `Google rejected this site's Maps key. The domain (${
    typeof window !== 'undefined' ? window.location.hostname : 'this site'
  }) probably is not on the key's allowed-referrers list in Google Cloud Console.`

if (typeof window !== 'undefined') {
  ;(window as any).gm_authFailure = () => {
    authFailed = true
    console.error('[GoogleMaps] ' + MAPS_AUTH_MESSAGE)
    for (const fn of authListeners) fn(MAPS_AUTH_MESSAGE)
  }
}

/** Whether Google has already refused the key this session. */
export function mapsAuthFailed(): boolean {
  return authFailed
}

/**
 * Be told if Google refuses the key — either now, if it already has, or when
 * it does. Returns an unsubscribe for effect cleanup.
 */
export function onMapsAuthFailure(fn: (message: string) => void): () => void {
  if (authFailed) fn(MAPS_AUTH_MESSAGE)
  authListeners.add(fn)
  return () => authListeners.delete(fn)
}

export function loadGoogleMaps(apiKey: string): Promise<void> {
  if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
    return Promise.resolve()
  }
  if (_mapsPromise) return _mapsPromise

  _mapsPromise = new Promise<void>((resolve, reject) => {
    const cbName = `__googleMapsReady_${Date.now()}`
    ;(window as any)[cbName] = () => {
      delete (window as any)[cbName]
      resolve()
    }
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&callback=${cbName}`
    script.async = true
    script.defer = true
    script.onerror = () => {
      _mapsPromise = null
      delete (window as any)[cbName]
      reject(new Error('Failed to load Google Maps'))
    }
    document.head.appendChild(script)
  })

  return _mapsPromise
}
